import React, { memo, useCallback, useEffect, useState } from "react";
import { IoIosArrowBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import Activity from "../Activity";
import {
  BASE_URL,
  getHelper,
  getToken,
  parseSafeDate,
  removeAccents,
  filterGuestEventsByActivityDay,
  getCalendarFetchRangeForLogicalAnchor,
  getGuestActivityDayStartMinutesFromGeneral,
  getLogicalActivityDayAnchorDate,
} from "../../general/helper";
import { useTranslation } from "react-i18next";
import { Button } from "@mui/material";

const Now = memo(({ lid, type, user, general, lang }) => {
  const params = new URLSearchParams(window.location.search);
  const filteredTagsToDisplay = params.get("tags") || "";
  const [activities, setActivities] = useState([]);
  const [isActivityOpen, setIsActivityOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState([]);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const goToHome = () => {
    const params = new URLSearchParams();

    if (lid) params.set("lid", String(lid));
    if (type) params.set("type", String(type));
    if (lang) params.set("lang", String(lang));

    // if it's array -> join, if it's string -> keep
    if (filteredTagsToDisplay && Array.isArray(filteredTagsToDisplay)) {
      if (filteredTagsToDisplay.length)
        params.set("tags", filteredTagsToDisplay.join(","));
    } else if (filteredTagsToDisplay) {
      params.set("tags", String(filteredTagsToDisplay));
    }

    const qs = params.toString();
    navigate(qs ? `/?${qs}` : "/");
  };

  const fetchEventsForToday = useCallback(async () => {
    try {
      const startMinutes = getGuestActivityDayStartMinutesFromGeneral(general);
      const anchor = getLogicalActivityDayAnchorDate(
        new Date(),
        startMinutes,
      );
      const { from, to } = getCalendarFetchRangeForLogicalAnchor(
        anchor,
        startMinutes,
      );
      const res = await getHelper(
        `${BASE_URL}/activitiesSchedule/guest/events?from=${from}&to=${to}`,
        { authorization: `token ${getToken()}` },
      );

      // ✅ backend returned an error object but no throw
      if (!res || res.error || res.status >= 400) {
        console.error("Backend error:", res);
        return [];
      }

      return Array.isArray(res) ? res : [];
    } catch (error) {
      // ✅ network error / server down / timeout
      console.error("Fetch events failed:", error);
      return [];
    }
  }, [general]);

  const filteredEventsByStartTime = useCallback((activities) => {
    const currentTime = new Date();

    const filteredActivities = activities
      .map((event) => {
        const startTime = parseSafeDate(event.start);
        const endTime = parseSafeDate(event.end);
        const isLive = startTime <= currentTime && endTime >= currentTime;
        if (filteredTagsToDisplay) {
          const hasDisplayTag = filteredTagsToDisplay
            ?.split(",")
            .some((tag) => event.activity.tags.includes(tag));
          if (hasDisplayTag) {
            return isLive ? { ...event, isLive } : undefined;
          }
          return undefined;
        } else {
          return isLive ? { ...event, isLive } : undefined;
        }
      })
      .filter((i) => i !== undefined);

    return filteredActivities;
  }, [filteredTagsToDisplay]);

  useEffect(() => {
    const fetchData = async () => {
      const startMinutes = getGuestActivityDayStartMinutesFromGeneral(general);
      const anchor = getLogicalActivityDayAnchorDate(
        new Date(),
        startMinutes,
      );
      const events = await fetchEventsForToday();
      const inWindow = filterGuestEventsByActivityDay(
        events,
        anchor,
        startMinutes,
      );
      const filteredActivities = filteredEventsByStartTime(inWindow);
      setActivities(filteredActivities);
    };

    fetchData();
  }, [fetchEventsForToday, filteredEventsByStartTime, general]);

  const openActivityModal = (activity) => {
    setSelectedActivity(activity);
    setIsActivityOpen(true);
  };

  const closeActivityModal = () => {
    setIsActivityOpen(false);
  };

  return (
    <div>
      <div className="flex items-start border-b-2 border-gray-300 py-4 ">
        <Button onClick={goToHome} className="mx-2 w-1/6">
          <IoIosArrowBack size={25} />
        </Button>
        <div className="w-2/3">
          <p className="text-center">
            {`${removeAccents(t("whatCanIdoNow"))}`}
          </p>
        </div>
      </div>
      {/* Render activities based on the filtered activities */}
      {activities?.map((activity) => (
        <div
          key={activity?.id}
          onClick={() => openActivityModal(activity)}
          className="h-24 bg-gradient-to-r from-purple-500 via-purple-600 to-purple-700 border-2 relative overflow-hidden rounded-lg mx-6 my-4"
        >
          {activity?.sliceImagesUrl && (
            <img
              src={`${BASE_URL}/${activity.sliceImagesUrl[0]}`}
              alt="Activity"
              className="mt-4 w-full h-24 object-cover rounded-lg"
            />
          )}
          <div className="h-fit w-3/4 absolute left-0 bottom-0 px-4 py-2">
            <div className="">
              <p className="text-white text-sm">
                {activity?.activity?.name?.[lang] || activity?.activity?.name?.en || activity?.activity?.name?.text}
              </p>
              <p className="text-white text-sm">
                {activity?.activity?.locationName?.[lang] || activity?.activity?.locationName?.en || activity?.activity?.locationName?.text}
              </p>
            </div>
          </div>
          <div className="h-full w-1/4 absolute top-0 right-0 px-4 py-2 backdrop-filter backdrop-blur bg-opacity-75 flex justify-center items-center">
            <p className="text-white text-sm">
              {activity?.start?.slice(10, 16)}
            </p>
          </div>
        </div>
      ))}
      {isActivityOpen && (
        <Activity
          lang={lang}
          user={user}
          selectedActivity={selectedActivity}
          isActivityOpen={isActivityOpen}
          closeActivityModal={closeActivityModal}
          general={general}
        />
      )}
    </div>
  );
});

export default Now;
